/** 56px confirm-screen button (prototype `.btn`): default (outlined) or primary (red). */
import { FC } from "react"
import {
  ActivityIndicator,
  Pressable,
  PressableStateCallbackType,
  TextStyle,
  ViewStyle,
} from "react-native"

import { Text } from "@/components/Text"

import { webTransition } from "./motion"
import { font, tokens } from "./tokens"

export interface ActionButtonProps {
  label: string
  variant?: "default" | "primary"
  disabled?: boolean
  /** Shows a spinner next to the label. */
  loading?: boolean
  onPress: () => void
  testID?: string
}

type HoverState = PressableStateCallbackType & { hovered?: boolean }

export const ActionButton: FC<ActionButtonProps> = ({
  label,
  variant = "default",
  disabled = false,
  loading = false,
  onPress,
  testID,
}) => {
  const primary = variant === "primary"
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityState={{ disabled, busy: loading }}
      disabled={disabled}
      onPress={onPress}
      testID={testID}
      style={(state) => {
        const hovered = (state as HoverState).hovered && !disabled
        return [
          $button,
          primary ? $primary : $default,
          hovered && !primary && $defaultHover,
          hovered && $lift,
          disabled && $disabled,
        ]
      }}
    >
      <Text style={[$label, primary && $labelPrimary]}>{label}</Text>
      {loading && <ActivityIndicator size="small" color={tokens.surface} />}
    </Pressable>
  )
}

const $button: ViewStyle = {
  flex: 1,
  minHeight: 56,
  borderRadius: 4,
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  paddingHorizontal: 12,
  ...webTransition("transform, box-shadow, background-color", 180),
}
const $default: ViewStyle = {
  backgroundColor: tokens.surface,
  borderWidth: 1,
  borderColor: tokens.btnBorder,
}
const $defaultHover: ViewStyle = { backgroundColor: tokens.mist }
const $primary: ViewStyle = { backgroundColor: tokens.red }
const $lift = {
  transform: [{ scale: 1.02 }],
  boxShadow: "0 6px 16px rgba(17, 30, 44, 0.2)",
} as ViewStyle
const $disabled: ViewStyle = { opacity: 0.7 }
const $label: TextStyle = {
  fontSize: 16,
  lineHeight: 20,
  fontFamily: font.medium,
  color: tokens.ink,
  textAlign: "center",
}
const $labelPrimary: TextStyle = { fontFamily: font.semi, color: tokens.surface }
